<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useWorkspaceStore } from '@/store/workspace'
import {
  getVariableIndexDisplayLabel,
  type VariableIndexScope,
} from '@/features/event/variableIndex'
import VariableIndexPickerModal from '@/components/eventEditors/VariableIndexPickerModal.vue'

/**
 * 变量索引选择字段
 *
 * 只读展示当前索引对应名称，点击输入框或按钮打开 `VariableIndexPickerModal` 选择索引。
 */
const props = defineProps<{
  modelValue: number
  scope: VariableIndexScope
  title?: string
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  allowNegativeOne?: boolean
  revision?: number
  displayLabel?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const workspace = useWorkspaceStore()

/** 选择弹窗是否打开。 */
const pickerOpen = ref(false)
/** 本地索引值，与 `modelValue` 同步。 */
const localValue = ref(props.modelValue)

watch(
  () => props.modelValue,
  (value) => {
    localValue.value = value
  },
)

/** 输入框展示的索引标签（优先 `displayLabel`，否则按工程解析）。 */
const selectedLabel = computed(() => {
  props.revision
  if (props.displayLabel !== undefined) return props.displayLabel
  return getVariableIndexDisplayLabel(localValue.value, workspace.project, props.scope, {
    allowNegativeOne: props.allowNegativeOne,
    placeholder: props.placeholder,
  })
})

/** 打开索引选择弹窗。 */
function openPicker() {
  if (props.disabled || props.readonly) return
  pickerOpen.value = true
}

/**
 * 确认选择后更新本地值并向外 emit。
 * @param value 选中的变量索引
 */
function updateValue(value: number) {
  localValue.value = value
  emit('update:modelValue', value)
}
</script>

<template>
  <div
    class="variable-index-field"
    :class="{
      'variable-index-field--disabled': disabled,
    }"
  >
    <input
      class="variable-index-field__display"
      type="text"
      inputmode="none"
      :value="selectedLabel"
      :placeholder="placeholder"
      :disabled="disabled"
      :title="selectedLabel"
      readonly
      @pointerdown.prevent="openPicker"
    />
    <button
      type="button"
      class="variable-index-field__trigger"
      :disabled="disabled || readonly"
      :title="title || selectedLabel"
      @click="openPicker"
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path
          d="M11.6 1.4a1.5 1.5 0 0 1 2.1 2.1l-7.3 7.3-3 0.8 0.8-3 7.4-7.2Zm1.4 2.1a.5.5 0 0 0-.7-.7L5.2 9.9l-0.4 1.4 1.4-0.4L13 3.5ZM2 13h12v1H2z"
          fill="currentColor"
        />
      </svg>
    </button>

    <VariableIndexPickerModal
      v-if="pickerOpen"
      v-model:show="pickerOpen"
      :title="title"
      :selected-index="localValue"
      :scope="scope"
      :allow-negative-one="allowNegativeOne"
      @select="updateValue"
    />
  </div>
</template>

<style scoped>
.variable-index-field {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 26px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  flex: 1 1 0;
  box-sizing: border-box;
}

.variable-index-field__display {
  height: 28px;
  border: 1px solid var(--theme-bg-secondary);
  background: var(--theme-bg-surface);
  color: var(--theme-text-heading);
  font-size: 13px;
  box-sizing: border-box;
}

.variable-index-field__display {
  min-width: 0;
  padding: 4px 8px;
  border-right: 0;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.variable-index-field__display:focus {
  outline: none;
  border-color: var(--theme-accent);
}

.variable-index-field__trigger {
  width: 26px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--theme-bg-secondary);
  background: var(--theme-bg-surface);
  color: var(--theme-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.variable-index-field__trigger:hover:not(:disabled) {
  color: var(--theme-text-heading);
}

.variable-index-field--disabled .variable-index-field__display {
  cursor: default;
}

.variable-index-field__trigger svg {
  width: 14px;
  height: 14px;
}
</style>
